import { Account, AccountType, getExternalPlatformIdFromInternalPlatformId, getVirtualEnvironment, VMType, 
// RPC types
ORBY_GET_OPERATIONS_TO_EXECUTE_TRANSACTION, ORBY_GET_OPERATIONS_TO_SIGN_TYPED_DATA, ORBY_GET_OPERATIONS_TO_SIGN_TRANSACTION_OR_SIGN_TYPED_DATA, ORBY_GET_OPERATIONS_FOR_STABLECOIN_AUTO_DEPOSIT_SETTINGS, ORBY_GET_OPERATIONS_TO_CANCEL_TRANSACTION, ORBY_IS_TRANSACTION_PRECONDITION_SATISFIED, ORBY_IS_TYPED_DATA_PRECONDITION_SATISFIED, ORBY_SEND_SIGNED_OPERATIONS, ORBY_GET_OPERATION_SET, ORBY_GET_OPERATION_STATUSES, ORBY_GET_OPERATIONS_TO_TRANSFER_TOKEN, ORBY_GET_OPERATIONS_TO_SWAP, ORBY_GET_QUOTE, notEmpty, OperationType, } from "@orb-labs/types";
import { createClient, http } from "viem";
import { ethers } from "ethers";
import { extractActivity, extractOperationSet, extractOperationStatuses } from "../utils/action_helpers.js";
import { ActivityStatus, LIBRARY_TYPE, OperationDataFormat, OperationOrigin, OperationStatusType, } from "../enums.js";
import { LibraryRequest } from "../actions/library_request.js";
import { hasError } from "../utils/utils";
// from here: https://stackoverflow.com/questions/65152373/typescript-serialize-bigint-in-json
BigInt.prototype.toJSON = function () {
    return this.toString();
};
export class OperationActions extends LibraryRequest {
    constructor(library, client, provider) {
        super(library, client, provider);
    }
    async getOperationsToExecuteTransaction(accountClusterId, data, to, value, gasToken) {
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_EXECUTE_TRANSACTION, [{ accountClusterId, to, data, value, gasToken: await this.formatGasToken(gasToken) }]);
        return extractOperationSet(operationSet);
    }
    async getOperationsToSignTypedData(accountClusterId, data, gasToken) {
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_SIGN_TYPED_DATA, [{ accountClusterId, data, gasToken: await this.formatGasToken(gasToken) }]);
        return extractOperationSet(operationSet);
    }
    async getOperationsToSignTransactionOrSignTypedData(accountClusterId, data, to, value, gasToken, options) {
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_SIGN_TRANSACTION_OR_SIGN_TYPED_DATA, [
            {
                accountClusterId,
                to,
                data,
                value,
                gasToken: await this.formatGasToken(gasToken),
                options: options ? Object.fromEntries(options) : undefined,
            },
        ]);
        return extractOperationSet(operationSet);
    }
    async getOperationsForStablecoinAutoDepositSettings(accountClusterId, settings, gasToken) {
        const formattedSettings = await Promise.all(settings.map(async (setting) => ({
            ...setting,
            platformId: getExternalPlatformIdFromInternalPlatformId(setting.platformId),
        })));
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_FOR_STABLECOIN_AUTO_DEPOSIT_SETTINGS, [{ accountClusterId, settings: formattedSettings, gasToken: await this.formatGasToken(gasToken) }]);
        return extractOperationSet(operationSet);
    }
    async getOperationsToCancelTransaction(accountClusterId, operationSetId) {
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_CANCEL_TRANSACTION, [{ accountClusterId, operationSetId }]);
        return extractOperationSet(operationSet);
    }
    async getOperationsToSetupOnPlatform(platformId, account) {
        const operationSet = await this.sendRequest("orby_getOperationsToSetupOnPlatform", [
            { account: account.toAccountModel(), platformId: getExternalPlatformIdFromInternalPlatformId(platformId) },
        ]);
        return extractOperationSet(operationSet);
    }
    async submitOperationsToSetupOnPlatform(input) {
        const setupResult = await this.sendRequest("orby_submitOperationsToSetupOnPlatform", [input]);
        if (hasError(setupResult)) {
            console.error("[submitOperationsToSetupOnPlatform]", setupResult.code, setupResult.message);
            return undefined;
        }
        return setupResult;
    }
    async isTransactionPreconditionSatisfied(accountClusterId, data, to, value, gasLimit, maxFeePerGas, maxPriorityFeePerGas, gasPrice) {
        const { satisfied } = await this.sendRequest(ORBY_IS_TRANSACTION_PRECONDITION_SATISFIED, [{ accountClusterId, to, data, value, gasLimit, maxFeePerGas, maxPriorityFeePerGas, gasPrice }]);
        return satisfied;
    }
    async isTypedDataPreconditionSatisfied(accountClusterId, data) {
        const { satisfied, code, message } = await this.sendRequest(ORBY_IS_TYPED_DATA_PRECONDITION_SATISFIED, [{ accountClusterId, data }]);
        if (code && message) {
            console.error("[isTypedDataPreconditionSatisfied]", code, message);
            return undefined;
        }
        return satisfied;
    }
    async sendSignedOperations(accountClusterId, signedOperations, primaryAccountPreExistingState) {
        const { operationSetId, operationResponses, code, message } = await this.sendRequest(ORBY_SEND_SIGNED_OPERATIONS, [
            {
                accountClusterId,
                primaryAccountPreExistingState: primaryAccountPreExistingState?.toAccountStateModel(),
                signedOperations: await Promise.all(signedOperations.map(async (signedOperation) => ({
                    ...signedOperation,
                    platformId: getExternalPlatformIdFromInternalPlatformId(signedOperation.platformId),
                }))),
            },
        ]);
        console.log("sendSignedOperations", signedOperations);
        if (code && message) {
            throw new Error(`[sendSignedOperations] ${code}: ${message}`);
        }
        return {
            operationSetId,
            operationResponses: extractOperationStatuses(operationResponses),
        };
    }
    async getOperationSet(operationSetId) {
        const { activity, code, message } = await this.sendRequest(ORBY_GET_OPERATION_SET, [
            { operationSetId },
        ]);
        if (code && message) {
            console.error("[getOperationSet]", code, message);
            return undefined;
        }
        return extractActivity(activity);
    }
    async getOperationStatuses(operationIds) {
        const { operationStatuses } = await this.sendRequest(ORBY_GET_OPERATION_STATUSES, [
            { operationIds },
        ]);
        return extractOperationStatuses(operationStatuses);
    }
    async getOperationsToTransferToken(accountClusterId, standardizedInstrumentId, amount, recipient, gasToken) {
        const formattedRecepient = {
            platformId: getExternalPlatformIdFromInternalPlatformId(recipient.platformId),
            address: recipient.address,
        };
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_TRANSFER_TOKEN, [
            {
                accountClusterId,
                standardizedInstrumentId,
                amount,
                recipient: formattedRecepient,
                gasToken: await this.formatGasToken(gasToken),
            },
        ]);
        return extractOperationSet(operationSet);
    }
    async getOperationsToSwap(accountClusterId, swapType, input, output, gasToken) {
        const formattedInput = input
            ? {
                ...input,
                tokenSources: input.tokenSources
                    ? await Promise.all(input.tokenSources.map(async ({ platformId, address }) => ({
                        platformId: getExternalPlatformIdFromInternalPlatformId(platformId),
                        address,
                    })))
                    : undefined,
            }
            : undefined;
        const formattedOutput = output
            ? {
                ...output,
                tokenDestination: output.tokenDestination
                    ? {
                        platformId: getExternalPlatformIdFromInternalPlatformId(output.tokenDestination.platformId),
                        address: output.tokenDestination.address,
                    }
                    : undefined,
            }
            : undefined;
        const operationSet = await this.sendRequest(ORBY_GET_OPERATIONS_TO_SWAP, [
            {
                accountClusterId,
                swapType,
                input: formattedInput,
                output: formattedOutput,
                gasToken: await this.formatGasToken(gasToken),
            },
        ]);
        return extractOperationSet(operationSet);
    }
    async getQuote(accountClusterId, swapType, input, output, paymentMethod, returnRouteInformation, meshBalances) {
        const formattedInput = input
            ? {
                ...input,
                tokenSources: input.tokenSources
                    ? await Promise.all(input.tokenSources.map(async ({ platformId, address }) => ({
                        platformId: getExternalPlatformIdFromInternalPlatformId(platformId),
                        address,
                    })))
                    : undefined,
            }
            : undefined;
        const formattedOutput = output
            ? {
                ...output,
                tokenDestination: output.tokenDestination
                    ? {
                        platformId: getExternalPlatformIdFromInternalPlatformId(output.tokenDestination.platformId),
                        address: output.tokenDestination.address,
                    }
                    : undefined,
            }
            : undefined;
        const operationSet = await this.sendRequest(ORBY_GET_QUOTE, [
            {
                accountClusterId,
                swapType,
                input: formattedInput,
                output: formattedOutput,
                paymentMethod,
                returnRouteInformation,
                meshBalances,
            },
        ]);
        return extractOperationSet(operationSet);
    }
    subscribeToOperationSetStatus(operationSetId, onOperationSetStatusUpdateCallback, timeout) {
        let intervalId = null;
        let totalWaitTime = 0;
        const timeoutOrDefault = timeout ?? 300_000;
        if (operationSetId) {
            const waitTime = 500;
            intervalId = setInterval(async () => {
                let statusSummary = ActivityStatus.WAITING_PRECONDITION;
                try {
                    const activity = await this?.getOperationSet(operationSetId);
                    if (activity) {
                        statusSummary = activity?.overallStatus;
                    }
                    onOperationSetStatusUpdateCallback?.(activity);
                }
                catch (error) {
                    console.error("[subscribeToOperationSetStatus]", error);
                }
                finally {
                    totalWaitTime += waitTime;
                    if (intervalId &&
                        ![
                            ActivityStatus.WAITING_TOKEN_APPROVAL_TRANSACTIONS,
                            ActivityStatus.WAITING_GAS_ABSTRACTION,
                            ActivityStatus.WAITING_PRECONDITION,
                        ].includes(statusSummary)) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                    else if (intervalId && totalWaitTime >= timeoutOrDefault) {
                        // we cant wait for more than 2 ETH blocks
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }
            }, waitTime);
        }
        return { intervalId };
    }
    subscribeToOperationStatuses(ids, onOperationStatusesUpdateCallback, timout) {
        let intervalId = null;
        let totalWaitTime = 0;
        const timeoutOrDefault = timout ?? 300_000;
        if (!ids || ids?.length == 0) {
            onOperationStatusesUpdateCallback?.(OperationStatusType.SUCCESSFUL, undefined, []);
        }
        else if (ids && ids?.length > 0) {
            const waitTime = 500;
            intervalId = setInterval(async () => {
                let statusSummary = OperationStatusType.WAITING_PRECONDITION;
                let primaryOperation;
                try {
                    const statuses = ids && ids?.length > 0 ? await this?.getOperationStatuses(ids) : undefined;
                    if (!ids || ids?.length == 0) {
                        statusSummary = OperationStatusType.NOT_FOUND;
                    }
                    else if (!statuses) {
                        statusSummary = OperationStatusType.NOT_FOUND;
                    }
                    else if (statuses.some((status) => status.status == OperationStatusType.FAILED)) {
                        statusSummary = OperationStatusType.FAILED;
                    }
                    else if (statuses.every((status) => status.status == OperationStatusType.SUCCESSFUL)) {
                        statusSummary = OperationStatusType.SUCCESSFUL;
                    }
                    primaryOperation = statuses?.find((status) => status.type == OperationType.FINAL_TRANSACTION);
                    if ([OperationStatusType.PENDING, OperationStatusType.SUCCESSFUL].includes(primaryOperation?.status)) {
                        statusSummary = primaryOperation?.status;
                    }
                    onOperationStatusesUpdateCallback?.(statusSummary, primaryOperation, statuses);
                }
                catch (error) {
                    console.error("[subscribeToOperationStatuses]", error);
                }
                finally {
                    totalWaitTime += waitTime;
                    if (intervalId &&
                        ![
                            OperationStatusType.WAITING_TOKEN_APPROVAL_TRANSACTIONS,
                            OperationStatusType.WAITING_GAS_ABSTRACTION,
                            OperationStatusType.WAITING_PRECONDITION,
                        ].includes(statusSummary)) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                    else if (intervalId && totalWaitTime >= timeoutOrDefault) {
                        // we cant wait for more than 2 ETH blocks
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }
            }, waitTime);
        }
        return { intervalId };
    }
    async sendOperationSet(accountCluster, operationSet, signTransaction, signUserOperation, signTypedData, submitOperation) {
        const signedOperations = [];
        const operations = operationSet.intents
            ?.map((intent) => intent.intentOperations)
            .flat()
            ?.concat(operationSet.primaryOperation)
            .filter((value) => notEmpty(value) && value?.origin !== OperationOrigin.EXTERNAL);
        if (operations?.length == 0) {
            return { success: true, operationResponses: [] };
        }
        else if (!operations) {
            return { success: false };
        }
        const accountsMap = accountCluster.accounts.reduce((acc, account) => {
            acc.set(account.key, account);
            return acc;
        }, new Map());
        const smartAccountTransactions = operations.reduce((acc, operation) => {
            const accountKey = Account.key(operation.from, operation.platformId);
            const account = accountsMap.get(accountKey);
            if (account?.type == AccountType.SMART_CONTRACT && operation.format == OperationDataFormat.TRANSACTION) {
                if (!acc.has(accountKey)) {
                    acc.set(accountKey, []);
                }
                acc.get(accountKey).push(operation);
            }
            return acc;
        }, new Map());
        // Loop through all the operations and sign them
        for (let i = 0; i < operations.length; i++) {
            const operation = operations[i];
            let signature;
            if (operation.format == OperationDataFormat.CENTRALIZED_EXCHANGE) {
                if (!submitOperation) {
                    throw new Error('Please provide a "submitOperation" function');
                }
                signature = await submitOperation(operation);
            }
            else if (operation.format == OperationDataFormat.TRANSACTION) {
                const accountKey = Account.key(operation.from, operation.platformId);
                const account = accountsMap.get(accountKey);
                const vmType = getVirtualEnvironment(operation.platformId);
                if (vmType == VMType.OFFCHAIN) {
                    if (!submitOperation) {
                        throw new Error('Please provide a "submitOperation" function');
                    }
                    signature = await submitOperation(operation);
                }
                else if (account?.type == AccountType.SMART_CONTRACT) {
                    // Smart contract account transactions are batch-signed below
                    continue;
                }
                else {
                    if (!signTransaction) {
                        throw new Error('Please provide a "signTransaction" function');
                    }
                    signature = await signTransaction(operation);
                }
            }
            else if (operation.format == OperationDataFormat.TYPED_DATA) {
                if (!signTypedData) {
                    throw new Error('Please provide a "signTypedData" function');
                }
                signature = await signTypedData(operation);
            }
            if (!signature) {
                return { success: false };
            }
            signedOperations.push({
                type: operation.type,
                signature,
                data: operation.data,
                from: operation.from,
                platformId: operation.platformId,
                inputAccountStates: operation.inputAccountStates?.toAccountStateModel(),
                outputAccountStates: operation.outputAccountStates?.toAccountStateModel(),
                executionIndex: operation.executionIndex,
            });
        }
        // batch sign all the smart contract transactions
        const accountKeys = Array.from(smartAccountTransactions.keys());
        for (const accountKey of accountKeys) {
            const transactions = smartAccountTransactions.get(accountKey);
            transactions.sort((operationA, _) => {
                if (operationA.type == OperationType.APPROVE_ERC20_TOKEN) {
                    return -1;
                }
                return 0;
            });
            const userOperation = await signUserOperation(transactions, transactions[0].from, transactions[0].platformId, transactions[0].txRpcUrl);
            if (!userOperation) {
                return { success: false };
            }
            const types = transactions.map((operation) => operation.type);
            let type = types[0];
            if (types.includes(OperationType.FINAL_TRANSACTION)) {
                type = OperationType.FINAL_TRANSACTION;
            }
            else if (types.includes(OperationType.SUBMIT_INTENT)) {
                type = OperationType.SUBMIT_INTENT;
            }
            signedOperations.push({
                type,
                signature: userOperation.signature,
                data: JSON.stringify(userOperation),
                from: transactions[0].from,
                platformId: transactions[0].platformId,
                inputAccountStates: transactions[0].inputAccountStates?.toAccountStateModel(),
                outputAccountStates: transactions[0].outputAccountStates?.toAccountStateModel(),
                executionIndex: transactions[0].executionIndex,
            });
        }
        const { operationSetId, operationResponses } = await this.sendSignedOperations(accountCluster.accountClusterId, signedOperations, operationSet.primaryOperationPreExistingState);
        return {
            operationSetId,
            operationResponses,
            success: true,
            primaryOperationStatus: {
                type: OperationType.FINAL_TRANSACTION,
                status: OperationStatusType.WAITING_PRECONDITION,
            },
        };
    }
    async sendOperationSetWithLocalOrchestration(accountCluster, operationSet, sendTransaction, sendUserOperation, signTypedData, onCompletionCallback, onOperationSubmissionCallback, timeout) {
        // 1. filter out external operations that are not handled by the client
        const orbyOperations = operationSet.intents
            ?.map((intent) => intent.intentOperations)
            .flat()
            ?.concat(operationSet.primaryOperation)
            .filter((value) => notEmpty(value) && value?.origin !== OperationOrigin.EXTERNAL);
        if (orbyOperations?.length == 0) {
            onCompletionCallback?.([], OperationStatusType.SUCCESSFUL);
            return;
        }
        else if (!orbyOperations) {
            onCompletionCallback?.([], OperationStatusType.FAILED);
            return;
        }
        // 2. group operations that can be sent in a single transaction together
        const accountsMap = accountCluster.accounts.reduce((acc, account) => {
            acc.set(account.key, account);
            return acc;
        }, new Map());
        const smartAccountTransactions = orbyOperations.reduce((acc, operation) => {
            const accountKey = Account.key(operation.from, operation.platformId);
            const account = accountsMap.get(accountKey);
            if (account?.type == AccountType.SMART_CONTRACT && operation.format == OperationDataFormat.TRANSACTION) {
                if (!acc.has(accountKey)) {
                    acc.set(accountKey, []);
                }
                acc.get(accountKey).push(operation);
            }
            return acc;
        }, new Map());
        const groupedOperations = [];
        for (let i = 0; i < orbyOperations.length; i++) {
            const operation = orbyOperations[i];
            if (operation.format == OperationDataFormat.TRANSACTION) {
                const accountKey = Account.key(operation.from, operation.platformId);
                const account = accountsMap.get(accountKey);
                if (account?.type == AccountType.SMART_CONTRACT) {
                    continue;
                }
                else {
                    groupedOperations.push([operation]);
                }
            }
            else if (operation.format == OperationDataFormat.TYPED_DATA) {
                groupedOperations.push([operation]);
            }
        }
        // 3. group the operations that can be sent in a single transaction together
        for (const accountKey of smartAccountTransactions.keys()) {
            const transactions = smartAccountTransactions.get(accountKey);
            transactions.sort((operationA, _) => {
                if (operationA.type == OperationType.APPROVE_ERC20_TOKEN) {
                    return -1;
                }
                return 0;
            });
            groupedOperations.push(transactions);
        }
        const timeoutOrDefault = timeout ?? 300_000;
        const waitTime = 2000;
        let intervalId = null;
        let totalWaitTime = 0;
        let isProcessing = false;
        const hashedOperations = new Array(groupedOperations.length).fill(undefined);
        // 3. send the operations in batches
        intervalId = setInterval(async () => {
            if (isProcessing) {
                return;
            }
            try {
                isProcessing = true;
                const promises = groupedOperations.map(async (operations, index) => {
                    if (hashedOperations[index]) {
                        return;
                    }
                    const operation = operations[0];
                    // create a new operation actions client for each operation
                    let opsActions;
                    if (this.library == LIBRARY_TYPE.VIEM) {
                        opsActions = new OperationActions(LIBRARY_TYPE.VIEM, createClient({
                            transport: http(operation.txRpcUrl),
                        }), undefined);
                    }
                    else if (this.library == LIBRARY_TYPE.ETHERS) {
                        opsActions = new OperationActions(LIBRARY_TYPE.ETHERS, undefined, new ethers.JsonRpcProvider(operation.txRpcUrl));
                    }
                    if (operation.format === OperationDataFormat.TRANSACTION) {
                        const isPreconditionSatisfied = await opsActions.isTransactionPreconditionSatisfied(accountCluster.accountClusterId, operation.data, operation.to, operation.value, operation.gasLimit, operation.maxFeePerGas, operation.maxPriorityFeePerGas, operation.gasPrice);
                        if (isPreconditionSatisfied) {
                            const accountKey = Account.key(operation.from, operation.platformId);
                            const account = accountsMap.get(accountKey);
                            if (account?.type == AccountType.SMART_CONTRACT) {
                                const hash = await sendUserOperation?.(operations, operation.from, operation.platformId, operation.txRpcUrl);
                                onOperationSubmissionCallback?.(hash, OperationStatusType.PENDING, operations);
                                hashedOperations[index] = {
                                    operationId: hash,
                                    status: OperationStatusType.SUCCESSFUL,
                                    operations,
                                };
                            }
                            else {
                                const hash = await sendTransaction?.(operation);
                                onOperationSubmissionCallback?.(hash, OperationStatusType.PENDING, operations);
                                hashedOperations[index] = {
                                    operationId: hash,
                                    status: OperationStatusType.SUCCESSFUL,
                                    operations,
                                };
                            }
                        }
                    }
                    else if (operation.format === OperationDataFormat.TYPED_DATA) {
                        const isPreconditionSatisfied = await opsActions.isTypedDataPreconditionSatisfied(accountCluster.accountClusterId, operation.data);
                        if (isPreconditionSatisfied) {
                            const signature = await signTypedData?.(operation);
                            const sendOperation = await opsActions.sendSignedOperations(accountCluster.accountClusterId, [
                                {
                                    type: operation.type,
                                    signature,
                                    data: operation.data,
                                    from: operation.from,
                                    platformId: operation.platformId,
                                    inputAccountStates: operation.inputState.toAccountStateModel(),
                                    outputAccountStates: operation.outputState.toAccountStateModel(),
                                },
                            ]);
                            onOperationSubmissionCallback?.(sendOperation.operationResponses[0].hash, OperationStatusType.SUCCESSFUL, operations);
                            hashedOperations[index] = {
                                operationId: sendOperation.operationResponses[0].hash,
                                status: OperationStatusType.SUCCESSFUL,
                                operations,
                            };
                        }
                    }
                });
                await Promise.all(promises);
                if (hashedOperations.every((result) => result)) {
                    onCompletionCallback?.(hashedOperations, OperationStatusType.SUCCESSFUL);
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
            catch (error) {
                console.error("[sendOperationSetWithLocalOrchestration]", error);
            }
            finally {
                isProcessing = false; // Reset processing flag
                totalWaitTime += waitTime;
                if (intervalId && totalWaitTime >= timeoutOrDefault) {
                    onCompletionCallback?.(hashedOperations, OperationStatusType.WAITING_PRECONDITION);
                    // we cant wait for more than 2 ETH blocks
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        }, waitTime);
    }
}
